import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Menu,
  MenuItem,
  Stack,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import logo from '../assets/logo.png';
import productsData from '../assets/menus/products.json';
import { appConfig } from '../configs/app-config';

export default function Header() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [productsExpanded, setProductsExpanded] = useState(false);

  const categories = useMemo(() => {
    return [...productsData].map(category => ({
      name: category.name,
      slug: category.routeTo
    }));
  }, []);

  return (
    <AppBar position="sticky">
      {/* Top Contact Bar (hide on very small screens if needed) */}
      <Box
        sx={{
          backgroundColor: 'primary.dark',
          color: '#fff',
          px: 2,
          py: 0.5,
          display: { xs: 'none', sm: 'block' },
        }}
      >
        <Stack direction="row" spacing={3} justifyContent="flex-end">
          <Stack direction="row" spacing={1} alignItems="center">
            <PhoneIcon fontSize="small" />
            <Typography component="a" href={`tel:${appConfig.appAuthorPhone}`} sx={{ color: '#fff', textDecoration: 'none' }}>
              {appConfig.appAuthorPhone}
            </Typography> &nbsp;/
            <Typography component="a" href={`tel:${appConfig.secodaryContactNumber}`} sx={{ color: '#fff', textDecoration: 'none' }}>
              {appConfig.secodaryContactNumber}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <EmailIcon fontSize="small" />
            <Typography component="a" href={`mailto:${appConfig.appAuthorEmail}`} sx={{ color: '#fff', textDecoration: 'none' }}>
            {appConfig.appAuthorEmail}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {/* Main Toolbar */}
      <Toolbar>
        {isMobile && (
          <IconButton color="inherit" edge="start" onClick={() => setDrawerOpen(true)}>
            <MenuIcon />
          </IconButton>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <img src={logo} alt={`${appConfig.appName} Logo`} style={{ height: 80, marginRight: 10 }} />
          <Typography variant="h6" fontSize={{ xs: 16, sm: 18 }}>
            {appConfig.appName}
          </Typography>
        </Box>

        {/* Desktop Menu */}
        {!isMobile && (
          <>
            <Button color="inherit" component={Link} to="/">Home</Button>

            <Button
              color="inherit"
              endIcon={<KeyboardArrowDownIcon />}
              onClick={(e) => setAnchorEl(e.currentTarget)}
            >
              Products
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              PaperProps={{
                sx: { minWidth: 150 }
              }}
            >
              {categories.map((category) => (
                <MenuItem
                  key={category.slug}
                  component={Link}
                  to={`/products/${category.slug}`}
                  onClick={() => setAnchorEl(null)}
                  sx={{ px: 3, py: 1.5 }}
                >
                  {category.name}
                </MenuItem>
              ))}
            </Menu>

            <Button color="inherit" component={Link} to="/about">About us</Button>
            <Button color="inherit" component={Link} to="/certificates">Certificates</Button>
            <Button color="inherit" component={Link} to="/contact">Contact</Button>
          </>
        )}
      </Toolbar>

      {/* Mobile Drawer */}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 260 }} role="presentation">
          <Box sx={{ p: 2, fontWeight: 600 }}>Menu</Box>
          <Divider />

          <List disablePadding>
            <ListItem button component={Link} to="/" onClick={() => setDrawerOpen(false)}>
              <ListItemText primary="Home" />
            </ListItem>

            <Accordion
              expanded={productsExpanded}
              onChange={() => setProductsExpanded(!productsExpanded)}
              disableGutters
              elevation={0}
              sx={{
                '&:before': { display: 'none' },
                backgroundColor: 'transparent'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ px: 2, minHeight: 48 }}
              >
                <Typography>Products</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List disablePadding>
                  {categories.map((category) => (
                    <ListItem
                      key={category.slug}
                      button
                      component={Link}
                      to={`/products/${category.slug}`}
                      onClick={() => {
                        setDrawerOpen(false);
                        setProductsExpanded(false);
                      }}
                      sx={{ pl: 4 }}
                    >
                      <ListItemText primary={category.name} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>

            <ListItem button component={Link} to="/about" onClick={() => setDrawerOpen(false)}>
              <ListItemText primary="About" />
            </ListItem>
            <ListItem button component={Link} to="/certificates" onClick={() => setDrawerOpen(false)}>
              <ListItemText primary="Certificates" />
            </ListItem>

            <ListItem button component={Link} to="/contact" onClick={() => setDrawerOpen(false)}>
              <ListItemText primary="Contact" />
            </ListItem>
          </List>

          <Divider />

          {/* Mobile Contact */}
          <Box sx={{ p: 2 }}>
            <Typography variant="body2">
              📞 {appConfig.appAuthorPhone}
            </Typography>
            <Typography variant="body2">
              ✉️ {appConfig.appAuthorEmail}
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </AppBar>
  );
}
